import { keepPreviousData, useQuery } from "@tanstack/react-query";
import axiosInstance from "@/api/axios-instance";
import { useMonitoring, type MonitoringScope } from "./monitoring-context";
import type {
  BbPizzaTimeline,
  BbSection,
  BbWebhook,
  BbWebhookFilters,
  BbWebhooksResponse,
  CamerasSection,
  DependenciesSection,
  EventFilters,
  EventGroup,
  EventsResponse,
  InstancesResponse,
  LiveSection,
  MobileApiResponse,
  MobileApiRowDetail,
  MobileApiStatus,
  MetricSeries,
  MonitoringWindow,
  PipelineSection,
  RuntimeSection,
  Summary,
} from "./types";

/**
 * Hand-written hooks of the monitoring API (not in the committed OpenAPI schema). Every query polls while
 * the board is visible and auto-refresh is on; the previous data stays on screen while a new window or
 * scope loads, so charts never flash empty.
 */

const BASE = "/api/admin/monitoring";

/** polling periods (ms) per endpoint, matched to how fast the data behind them changes */
export const POLL = {
  summary: 10_000,
  live: 5_000,
  runtime: 15_000,
  metrics: 30_000,
  pipeline: 30_000,
  bb: 30_000,
  bbWebhooks: 15_000,
  bbPizza: 15_000,
  mobileApi: 10_000,
  cameras: 30_000,
  dependencies: 30_000,
  events: 15_000,
  groups: 30_000,
  instances: 60_000,
} as const;

const scopeParams = (scope: MonitoringScope) =>
  scope.instance ? { instance: scope.instance } : scope.all ? { scope: "all" } : {};

const get = async <T,>(path: string, params?: Record<string, unknown>, signal?: AbortSignal) => {
  const { data } = await axiosInstance.get<T>(`${BASE}${path}`, { params, signal });
  return data;
};

function usePolled<T>(key: unknown[], path: string, period: number, params?: Record<string, unknown>,
                      enabled = true) {
  const { paused } = useMonitoring();
  return useQuery({
    queryKey: ["monitoring", ...key],
    queryFn: ({ signal }) => get<T>(path, params, signal),
    refetchInterval: paused ? false : period,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: !paused,
    placeholderData: keepPreviousData,
    staleTime: Math.min(period, 5_000),
    retry: 1,
    enabled,
  });
}

export const useMonitoringSummary = () => usePolled<Summary>(["summary"], "/summary", POLL.summary);

export const useMonitoringLive = () => usePolled<LiveSection>(["live"], "/live", POLL.live);

export const useMonitoringRuntime = () => usePolled<RuntimeSection>(["runtime"], "/runtime", POLL.runtime);

export function useMonitoringMetrics(window?: MonitoringWindow) {
  const { window: boardWindow, scope } = useMonitoring();
  const w = window ?? boardWindow;
  const params = { window: w, ...scopeParams(scope) };
  return usePolled<MetricSeries>(["metrics", w, scope.instance, scope.all], "/metrics", POLL.metrics, params);
}

export function useMonitoringPipeline(window?: MonitoringWindow) {
  const { window: boardWindow } = useMonitoring();
  const w = window ?? boardWindow;
  return usePolled<PipelineSection>(["pipeline", w], "/pipeline", POLL.pipeline, { window: w });
}

export function useMonitoringBb(window?: MonitoringWindow) {
  const { window: boardWindow } = useMonitoring();
  const w = window ?? boardWindow;
  return usePolled<BbSection>(["bb", w], "/bb", POLL.bb, { window: w });
}

/** BiteBerry webhook log; the window of the board bounds it unless a pizza / BB line is asked for. */
export function useBbWebhooks(filters: BbWebhookFilters, sinceMs: number) {
  const params = {
    since_ms: sinceMs,
    kind: filters.kinds.join(",") || undefined,
    outcome: filters.outcomes.join(",") || undefined,
    branch_id: filters.branchId || undefined,
    q: filters.q.trim() || undefined,
    item_id: filters.itemId || undefined,
    limit: filters.limit,
  };
  return usePolled<BbWebhooksResponse>(["bb-webhooks", params], "/bb/webhooks", POLL.bbWebhooks, params);
}

/** One webhook with its stored payload (loaded when a row is opened; the log never changes it). */
export function useBbWebhook(id: string | null) {
  return useQuery({
    queryKey: ["monitoring", "bb-webhook", id],
    queryFn: ({ signal }) => get<BbWebhook>(`/bb/webhooks/${encodeURIComponent(id ?? "")}`, undefined, signal),
    enabled: !!id,
    staleTime: Infinity,
    retry: 1,
  });
}

/** Everything about one pizza / BB line (polled while its panel is open). */
export const useBbPizza = (itemId: string | null) =>
  usePolled<BbPizzaTimeline>(["bb-pizza", itemId], `/bb/pizzas/${encodeURIComponent(itemId ?? "")}`, POLL.bbPizza,
    undefined, !!itemId);

/** The mobile API log of the board window (or of one pizza), newest first. */
export function useMobileApiLog(sinceMs: number, filters: { status: MobileApiStatus | null; q: string; pizzaId: string | null; limit: number }) {
  const params = {
    since_ms: sinceMs,
    status: filters.status || undefined,
    q: filters.q.trim() || undefined,
    pizza_id: filters.pizzaId || undefined,
    limit: filters.limit,
  };
  return usePolled<MobileApiResponse>(["mobile-api", params], "/mobile-api", POLL.mobileApi, params);
}

/** One logged request with its headers and both bodies (loaded when a row is opened; the log never changes it). */
export function useMobileApiRow(id: string | null) {
  return useQuery({
    queryKey: ["monitoring", "mobile-api-row", id],
    queryFn: ({ signal }) => get<MobileApiRowDetail>(`/mobile-api/${encodeURIComponent(id ?? "")}`, undefined, signal),
    enabled: !!id,
    staleTime: Infinity,
    retry: 1,
  });
}

export const useMonitoringCameras = () => usePolled<CamerasSection>(["cameras"], "/cameras", POLL.cameras);

export const useMonitoringDependencies = () =>
  usePolled<DependenciesSection>(["dependencies"], "/dependencies", POLL.dependencies);

export function useMonitoringEvents(filters: EventFilters) {
  const { scope } = useMonitoring();
  const params = {
    ...scopeParams(scope),
    level: filters.levels.join(",") || undefined,
    source: filters.sources.join(",") || undefined,
    q: filters.q.trim() || undefined,
    pizza_id: filters.pizzaId.trim() || undefined,
    fingerprint: filters.fingerprint || undefined,
    since_ms: filters.sinceMs ?? undefined,
    limit: filters.limit,
  };
  return usePolled<EventsResponse>(["events", params], "/events", POLL.events, params);
}

export function useMonitoringEventGroups(window?: MonitoringWindow, levels: string[] = []) {
  const { window: boardWindow, scope } = useMonitoring();
  const w = window ?? boardWindow;
  const params = { window: w, level: levels.join(",") || undefined, ...scopeParams(scope) };
  return usePolled<{ groups: EventGroup[]; window: string }>(["event-groups", params], "/events/groups",
    POLL.groups, params);
}

export const useMonitoringInstances = () =>
  usePolled<InstancesResponse>(["instances"], "/instances", POLL.instances);

/** message of a failed request (backend `{error}` body, or the transport error) */
export function errorMessage(error: unknown): string {
  const response = (error as { response?: { data?: { error?: string }; status?: number } })?.response;
  if (response?.data?.error) return response.data.error;
  if (response?.status) return `HTTP ${response.status}`;
  return error instanceof Error ? error.message : String(error);
}
